<script>
  import './projectPreview.css'
  import { ethers } from 'ethers'
  import { getProjectMetaService, salesQuery } from '../../../services/api'
  import { getProjectDetailService } from '../../../services/projectDetailService'
  import { onMount } from 'svelte'
  onMount(() => {
    window.scrollTo(0, 0)
  })
  import {
    isLoaderActive,
    isTransactionConfirmed,
    projectDetail,
    projectSaleDetails,
    tokenPurchased,
  } from '../../store'
  import SaleArtifact from '../../abi/Sale.json'
  import { signer, signerAddress } from 'svelte-ethers-store'
  import {
    formatNumber,
    getAfterTimestampDate,
    getAfterTimeStampStrapi,
    getERC20,
    getTokenPrice,
    getWalletCap,
    getProjectStatus,
  } from '../../utils'
  import ProjectCardPreview from './project-card-preview/ProjectCardPreview.svelte'
  import ProjectPreviewTab from './project-preview-tab/ProjectPreviewTab.svelte'
  import ProjectDetailPreview from './project-preview-tab/project-detail-preview/ProjectDetailPreview.svelte'
  import { formatUnits } from 'ethers/lib/utils'
  import ProjectTokenDetail from './project-preview-tab/project-token-detail/ProjectTokenDetail.svelte'
  import ProjectTransactionTab from './project-preview-tab/project-transaction-tab/ProjectTransactionTab.svelte'
  import { getSaleTransactionMetaService } from '../../../services/saleTransactionApi'
  import {
    PARTIAL_TIME_FORMAT,
    transactionType,
    COMPLETE_TIME_FORMAT,
  } from '../../constants/constants'
  import ProjectReward from './project-preview-tab/project-reward/ProjectReward.svelte'

  export let params = {}
  let currentStep = 1

  let tokenValues,
    reserveValues,
    saleContract,
    saleData,
    projectTierData,
    projectMeta,
    fakeProjectDetails

  let saleCardDetails = {
    saleAddress: $projectDetail.sale_address,
    raiseTarget: $projectDetail.project_raise_target,
    totalSupply: $projectDetail.total_available_token,
    startTime: new Date($projectDetail.project_start_time).getTime(),
    endTime: new Date($projectDetail.project_end_time).getTime(),
    walletCap: $projectDetail.individual_purchase_limit,
    totalRaised: '',
    price: $projectDetail.project_token_price,
    percentRaised: '',
    tokenName: $projectDetail.token_name,
    tokenSymbol: $projectDetail.token_symbol,
  }

  const setTokenPrice = async () => {
    if (params.address) {
      const { sale, reserve, token } = await initSaleContract(
        $signer,
        params.address,
      )
      saleContract = sale
      reserveValues = reserve
      tokenValues = token
    }
  }

  $: if ($signer) {
    if ($signer._address) setTokenPrice()
  }

  const setCurrentStep = (newStep) => {
    currentStep = newStep
  }

  const setFakeRaiseDetail = (projectDetailResp) => {
    projectDetailResp.progress_bar_status =
      projectDetailResp.progress_bar_status ?? 0
    saleCardDetails = {
      walletCap: projectDetailResp.individual_purchase_limit,
      raiseTarget: projectDetailResp.project_raise_target,
      totalSupply: projectDetailResp.total_available_token,
      startTime: new Date(projectDetailResp.project_start_time).getTime(),
      endTime: new Date(projectDetailResp.project_end_time).getTime(),
      totalRaised:
        ((projectDetailResp.progress_bar_status *
          projectDetailResp.total_available_token) /
          100) *
        projectDetailResp.project_token_price,
      price: projectDetailResp.project_token_price,
      tokenName: projectDetailResp.token_name,
      tokenSymbol: projectDetailResp.token_symbol,
      percentRaised: projectDetailResp.progress_bar_status,
    }
  }

  const setProjectDetail = async () => {
    let projectDetailResp = await getProjectDetailService(params.projectId)
    let { project_start_time, project_end_time } = projectDetailResp
    projectDetailResp.project_status = getProjectStatus(
      project_start_time,
      project_end_time,
    )
    projectDetail.set(projectDetailResp)
    if (!params.address) {
      setFakeRaiseDetail(projectDetailResp)
    }
  }

  const getSalesTransactions = async () => {
    tokenPurchased.set(0)
    if ($signerAddress && params.address) {
      const transactions = await getSaleTransactionMetaService(
        params.address,
        $signerAddress,
      )
      let sum = 0
      transactions.data.saleTransactions.forEach((transaction) => {
        sum +=
          transaction.__typename === transactionType.Buy
            ? formatNumber(
                transaction.receipt.units,
                transaction.saleContract.token.decimals,
              )
            : -formatNumber(
                transaction.receipt.units,
                transaction.saleContract.token.decimals,
              )
      })
      tokenPurchased.set(sum)
    }
  }

  signerAddress.subscribe(async () => await getSalesTransactions())

  const setProjectData = async () => {
    const { data } = await getProjectMetaService(params.address)
    projectMeta = data.sales[0]
    saleData = await salesQuery(params.address)
    setSaleData(data.sales[0])
    await getSalesTransactions()
  }

  const setSaleData = (sale) => {
    projectSaleDetails.set(sale)
    saleCardDetails = {
      project_address: projectSaleDetails.sale_address,
      raiseTarget: Number(
        formatUnits(sale.minimumRaise, sale.reserve.decimals),
      ),
      totalSupply: Number(
        +formatUnits(sale.token.totalSupply, sale.token.decimals),
      ),
      startTime: getAfterTimestampDate(
        sale.canStartStateConfig,
        COMPLETE_TIME_FORMAT,
      ).toLocaleString(),
      endTime: getAfterTimestampDate(
        sale.canEndStateConfig,
        COMPLETE_TIME_FORMAT,
      ).toLocaleString(),
      walletCap: getWalletCap(sale),
      totalRaised: Number(
        +formatUnits(sale.totalRaised, sale.reserve.decimals),
      ),
      price: getTokenPrice(sale),
      tokenName: sale.token.name,
      tokenSymbol: sale.token.symbol,
      percentRaised: sale.percentRaised,
      saleStatus: sale.saleStatus,
    }
  }

  export const initSaleContract = async (signer, address) => {
    isLoaderActive.set(true)
    const sale = new ethers.Contract(address, SaleArtifact.abi, signer)
    const reserve = await getERC20(await sale.reserve(), signer)
    const token = await getERC20(await sale.token(), signer)
    isLoaderActive.set(false)
    return { sale, reserve, token }
  }

  $: if ($isTransactionConfirmed) {
    setProjectData()
    setProjectDetail()
    ;(async () => await getSalesTransactions())()
    isTransactionConfirmed.set(false)
  }

  let details
  projectDetail.subscribe((value) => (details = value))

  if (params.projectId) {
    if (params.address) setProjectData()
    setProjectDetail()
  }

  const socialLinks = () => {
    let {
      facebook_url,
      instagram_url,
      twitch_url,
      twitter_url,
      youtube_url,
      linked_in_url,
      discord_url,
      telegram_url,
    } = $projectDetail

    return {
      facebook_url,
      instagram_url,
      twitch_url,
      twitter_url,
      youtube_url,
      linkedin: linked_in_url,
      discord_url,
      telegram_url,
    }
  }
</script>

<div class="project-preview-setup">
  <div class="container">
    <ProjectCardPreview
      {projectTierData}
      {saleCardDetails}
      {saleData}
      sale={saleContract}
      reserve={reserveValues}
      token={tokenValues} />
    <div class="flex social-networks-wrap">
      {#if $projectDetail.facebook_url || $projectDetail.instagram_url || $projectDetail.twitch_url || $projectDetail.twitter_url || $projectDetail.youtube_url || $projectDetail.linked_in_url || $projectDetail.discord_url || $projectDetail.telegram_url}
        <div class="detail-social flex items-center">
          <strong>Social Network:</strong>
          {#each Object.entries(socialLinks()) as [key, url]}
            {#if url}
              <a
                href={url.includes('http') ? url : `https://${url}`}
                target="_blank">
                <img
                  src={`./images/ic_${key.replaceAll('_url', '')}.svg`}
                  alt={key.replaceAll('_url', '')} />
              </a>
            {/if}
          {/each}
        </div>
      {/if}
    </div>
  </div>
  <ProjectPreviewTab
    {setCurrentStep}
    {currentStep}
    {saleContract}
    rewardVisible={details && details.rewards} />
  <div class="tabs-section">
    <div class="container">
      {#if currentStep === 1}
        <ProjectDetailPreview source={details && details.project_story} />
      {/if}
      {#if currentStep === 2}
        <ProjectTokenDetail source={details && details.token_details} />
      {/if}
      {#if currentStep === 3}
        <ProjectReward source={details && details.rewards} />
      {/if}
      {#if currentStep === 4}
        <ProjectTransactionTab
          endTime={saleCardDetails.endTime}
          {saleContract}
          bind:currentStep />
      {/if}

    </div>
  </div>
</div>
